<script setup lang="ts">
const { isLoggedIn } = useAuth();
const localePath = useLocalePath();

const businessStore = useBusinessStore();
const { selectedLocation, cartCount, business } = storeToRefs(businessStore);
const { openMenu } = businessStore;
const globalStore = useGlobalStore();
const { goingToSignInFromRoute } = storeToRefs(globalStore);

const cartCookie =
  useCookie<
    { location_id?: number; product_location_id: number; quantity: number }[]
  >("cart");
const route = useRoute();

// const keys = route?.meta?.keys as string[] | undefined;

const cartItemCount = computed(() => {
  if (isLoggedIn.value) {
    return cartCount.value;
  }

  return (
    cartCookie.value
      ?.filter((item) => item.location_id === selectedLocation.value?.id)
      .reduce((sum, item) => sum + item.quantity, 0) || 0
  );
});

const handleSignIn = (): void => {
  goingToSignInFromRoute.value = route.fullPath;
  globalStore.openSignInModal();
};
</script>

<template>
  <div class="flex items-center justify-between gap-2">
    <!-- Menu Button -->
    <div class="flex items-center min-w-0 flex-1 gap-x-1">
      <div v-if="!business?.is_freemium" class="shrink-0 flex items-center">
        <UButton
          icon="i-approved-menu"
          size="xl"
          color="gray"
          variant="ghost"
          class="rounded-xl relative"
          :ui="{ square: { xl: 'p-2' } }"
          @click="openMenu">
          <span
            v-if="cartItemCount"
            class="absolute bottom-1 right-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-furious-purple rounded-full">{{ cartItemCount }}</span>
        </UButton>
      </div>

      <!-- Location Info -->
      <NuxtLink :to="localePath(`/${route.params.location_identifier}`)" class="flex items-center gap-3 min-w-0 no-underline hover:no-underline">
        <!-- Logo -->
        <div v-if="selectedLocation?.logo" class="shrink-0">
          <img :src="selectedLocation?.logo" :alt="selectedLocation?.name" class="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover">
        </div>

        <!-- Name & Market -->
        <div class="flex flex-col min-w-0">
          <h2 class="block text-gray-900 text-lg sm:text-xl font-bold capitalize truncate">
            {{ selectedLocation?.name }}
          </h2>
          <p class="text-gray-500 text-xs sm:text-sm ltr:first-letter:uppercase truncate">
            {{ selectedLocation?.targeted_market }}
          </p>
        </div>
      </NuxtLink>
    </div>

    <!-- Account Actions -->
    <div class="shrink-0 flex items-center lg:hidden min-w-0 gap-2">
      <template v-if="!isLoggedIn">
        <!-- icon-only on very small screens -->
        <UButton
          icon="i-approved-sign-in"
          size="xl"
          color="dodger-blue"
          variant="solid"
          class="sm:hidden rounded-full"
          :ui="{ square: { xl: 'p-2' } }"
          @click="handleSignIn" />

        <!-- labeled button for small screens and up -->
        <UButton
          :label="$t('label.sign_in')"
          size="xl"
          :trailing="true"
          color="dodger-blue"
          class="hidden sm:inline-flex"
          :ui="{
            rounded: 'rounded-full',
            size: { xl: 'lg:text-base' },
            gap: { xl: 'gap-x-1' },
            padding: { xl: 'px-3 xs:px-3.5 py-2 xs:py-2.5' },
          }"
          @click="handleSignIn" />
      </template>
      <template v-else>
        <CommonClientDropdown />
      </template>
    </div>
  </div>
</template>
